import React, { useEffect, useState } from 'react';
import * as Unicons from '@iconscout/react-unicons';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';

// COMPONENTS
import TrackerHeader from '../shared/tracker-header';
import SubmitBar from '../shared/submit-bar';
import TrackerInstructions from '../shared/TrackerInstructions';
import {
  AfternoonIcon,
  BedtimeIcon,
  EveningIcon,
  HelperIcon,
  MorningIcon,
} from '../../assets/icons';

// QUERIES
import { DailyUserData } from '../../libs/graphql/queries/data';

// TRACKING
import analytics from '../../libs/analytics';
import MixpanelService, {
  MIXPANEL_DAY_FORMAT,
  MIXPANEL_TIME_FORMAT,
  MixpanelEvents,
} from '../../libs/mixpanel';

const DAY_TIMES = [
  {
    label: 'Morning',
    value: 'morning',
    Icon: MorningIcon,
  },
  {
    label: 'Afternoon',
    value: 'afternoon',
    Icon: AfternoonIcon,
  },
  {
    label: 'Evening',
    value: 'evening',
    Icon: EveningIcon,
  },
  {
    label: 'Night',
    value: 'bedtime',
    Icon: BedtimeIcon,
  },
];

const TreatmentTracker = ({
  id,
  theme,
  icon,
  settings,
  data,
  treatments,
  date,
  changeHandler,
  resetHandler,
}) => {
  const [write, setWrite] = useState(true);
  const [instructions, setInstructions] = useState(false);
  const [selectedTime, setSelectedTime] = useState(DAY_TIMES[0].value);
  const [selectedTreatments, setSelectedTreatments] = useState({
    morning: [],
    afternoon: [],
    evening: [],
    bedtime: [],
  });
  const [collapsedDayTimes, setCollapsedDayTimes] = useState([]);

  const resetStatus = () => {
    setSelectedTime(DAY_TIMES[0].value);
    setCollapsedDayTimes([]);
  };

  const buildTreatmentsData = data => {
    const treatmentsData = {
      morning: [],
      afternoon: [],
      evening: [],
      bedtime: [],
    };
    data.forEach(item => {
      Object.keys(treatmentsData).forEach(key => {
        if (item[key] === true) {
          treatmentsData[key].push(item.name);
        }
      });
    });
    setSelectedTreatments(treatmentsData);

    setWrite(data?.length === 0);
    if (data?.length === 0) {
      resetStatus();
    }
  };

  useEffect(() => {
    buildTreatmentsData(data);
  }, [data, date]);

  const toggleWrite = () => {
    setWrite(true);
  };

  const toggleInstructions = () => {
    setInstructions(!instructions);
  };

  const progressBadge = () => {
    if (!write) {
      return 'tracker-progress-complete';
    } else {
      return 'tracker-progress-incomplete';
    }
  };

  const _flattenTreatments = () => {
    const newData = data.map(item => ({
      treatmentID: item.id,
      morning: false,
      afternoon: false,
      evening: false,
      bedtime: false,
    }));

    Object.keys(selectedTreatments).forEach(key => {
      selectedTreatments[key].forEach(treatmentName => {
        const treatmentID = treatments.find(
          treatment => treatment.name === treatmentName,
        )?.id;

        if (!treatmentID) return;

        const dataIndex = newData.findIndex(
          item => item.treatmentID === treatmentID,
        );
        if (dataIndex !== -1) {
          newData[dataIndex][key] = true;
        } else {
          newData.push({
            treatmentID,
            morning: false,
            afternoon: false,
            evening: false,
            bedtime: false,
            [key]: true,
          });
        }
      });
    });

    return newData;
  };

  const onCancel = () => {
    if (data.length) {
      buildTreatmentsData(data);
    }
  };

  const onCancelPress = () => {
    setWrite(false);
    buildTreatmentsData(data);
    resetStatus();
  };

  const onSubmit = async () => {
    const newData = _flattenTreatments();

    return changeHandler({
      variables: {
        id: id,
        notes: '',
        client_date: date || moment().format('YYYY-MM-DD'),
        data: newData,
      },
      refetchQueries: () => [
        {
          query: DailyUserData,
          variables: {
            id: id,
            today: date || moment().format('YYYY-MM-DD'),
            readOnly: true,
          },
          fetchPolicy: 'cache-and-network',
        },
      ],
    }).then(res => {
      const Notes = '';
      MixpanelService.track(MixpanelEvents.TreatmentTrackerUpdated, {
        Morning: selectedTreatments.morning.join(', '),
        Afternoon: selectedTreatments.afternoon.join(', '),
        Evening: selectedTreatments.evening.join(', '),
        Bedtime: selectedTreatments.bedtime.join(', '),
        TreatmentDate: moment
          .utc(date)
          .format(`${MIXPANEL_DAY_FORMAT}T${MIXPANEL_TIME_FORMAT}`),
        Notes,
      });
      analytics().logEvent('any_track_item_updated', {});

      setWrite(false);
    });
  };

  const onReset = () => {
    resetHandler({
      variables: {
        id: id,
        tracker: 'TREATMENTS',
        client_date: date || moment().format('YYYY-MM-DD'),
      },
      refetchQueries: () => [
        {
          query: DailyUserData,
          variables: {
            id: id,
            today: date || moment().format('YYYY-MM-DD'),
            readOnly: true,
          },
          fetchPolicy: 'no-cache',
        },
      ],
    }).then(res => {
      setWrite(true);
      MixpanelService.track(MixpanelEvents.TreatmentTrackerReset, {
        TreatmentDate: date,
      });
    });
  };

  const toggleTreatment = treatmentName => {
    const dayTimeTreatments = [...selectedTreatments[selectedTime]];
    if (dayTimeTreatments.includes(treatmentName)) {
      dayTimeTreatments.splice(dayTimeTreatments.indexOf(treatmentName), 1);
    } else {
      dayTimeTreatments.push(treatmentName);
    }
    setSelectedTreatments({
      ...selectedTreatments,
      [selectedTime]: dayTimeTreatments,
    });
  };

  const isTreatmentSelected = treatmentName => {
    return selectedTreatments[selectedTime].includes(treatmentName);
  };

  const toggleDayTime = dayTime => {
    const newVisibleDayTimes = collapsedDayTimes.includes(dayTime)
      ? collapsedDayTimes.filter(d => d !== dayTime)
      : [...collapsedDayTimes, dayTime];
    setCollapsedDayTimes(newVisibleDayTimes);
  };

  const renderReadView = () => {
    return (
      <>
        <h4>Applied treatments</h4>
        <div className="day-times-list">
          {DAY_TIMES.map(dayTime => {
            if (selectedTreatments[dayTime.value].length === 0) return null;
            return (
              <>
                <div
                  className="day-time day-time--selected"
                  key={dayTime.value}
                  onClick={() => toggleDayTime(dayTime.value)}>
                  <div className="day-time__icon-button">
                    <dayTime.Icon isSelect={true} />
                  </div>
                  <span className="day-time__label">{dayTime.label}</span>
                  {collapsedDayTimes.includes(dayTime.value) ? (
                    <Unicons.UilAngleDown size="24" />
                  ) : (
                    <Unicons.UilAngleUp size="24" />
                  )}
                </div>
                {!collapsedDayTimes.includes(dayTime.value) && (
                  <div className="selected-treatment-options">
                    {selectedTreatments[dayTime.value].map(treatmentName => (
                      <div className="selected-treatment-item">
                        <span className="selected-treatment-item__label">
                          {treatmentName}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })}
        </div>
      </>
    );
  };

  const onClickSettings = () => {
    document.body.scrollTop = 0;
  };

  const renderEditView = () => {
    return (
      <>
        <h4>Select time and treatments applied.</h4>
        <div className="edit-view">
          <div className="day-times">
            {DAY_TIMES.map(dayTime => (
              <div
                onClick={() => setSelectedTime(dayTime.value)}
                className={`day-time ${
                  selectedTime === dayTime.value ? 'day-time--selected' : ''
                }`}
                key={dayTime.value}>
                <div className="day-time__icon-button">
                  <dayTime.Icon isSelect={selectedTime === dayTime.value} />
                </div>
                <span className="day-time__label">
                  {dayTime.label}{' '}
                  {selectedTreatments[dayTime.value].length > 0
                    ? `(${selectedTreatments[dayTime.value].length})`
                    : ''}
                </span>
              </div>
            ))}
          </div>
          <div className="treatment-option-list">
            {treatments.map(treatment => {
              if (!showTreatment(treatment)) return null;
              return (
                <div
                  className={`treatment-option-item ${
                    isTreatmentSelected(treatment.name) ? 'selected' : ''
                  }`}
                  onClick={() => toggleTreatment(treatment.name)}
                  key={treatment.id}>
                  <div className="treatment-option-item__checkbox">
                    <Unicons.UilCheck size="16" />
                  </div>
                  <span className="treatment-option-item__label">
                    {treatment.name}
                  </span>
                </div>
              );
            })}
            <Link
              to="/treatments"
              onClick={onClickSettings}
              className="treatment-option-item">
              <div className="treatment-option-item__add">
                <Unicons.UilPlus size="16" />
              </div>
              <span className="treatment-option-item__label">
                <strong>Add treatments</strong>
              </span>
            </Link>
          </div>
        </div>
      </>
    );
  };

  const renderInstructions = () => {
    return <TrackerInstructions instructions={theme.instructionsText} />;
  };

  const showTreatment = treatment => {
    const { active, name } = treatment;
    const isSelected = data.some(
      d =>
        d.name === name && (d.morning || d.afternoon || d.evening || d.bedtime),
    );
    return active || isSelected;
  };

  const hasChanges = Object.values(selectedTreatments).some(
    array => array.length > 0,
  );

  return (
    <div
      className={`card tracker tracker-loaded tracker-${
        theme.title
      } ${progressBadge()} ${theme.class || ''} ${
        write
          ? theme.color + ' card__active tracker__active'
          : theme.color + ' tracker__inactive'
      }`}>
      <TrackerHeader
        title="Treatments"
        write={write}
        icon={icon}
        settings={settings}
        edit={toggleWrite}
        instructions={toggleInstructions}
        instructionsClass={instructions ? 'tracker-header__instructions' : ''}
      />
      <div className="tracker-body tracker-body__scroll treatment-tracker">
        {instructions
          ? renderInstructions()
          : write
          ? renderEditView()
          : renderReadView()}
      </div>
      {!instructions && (
        <SubmitBar
          onCancel={onCancelPress}
          onSubmit={onSubmit}
          disableSubmit={instructions}
          disableCancel={instructions}
          hasChanged={hasChanges}
          hasReset={!write}
          onReset={onReset}
        />
      )}
    </div>
  );
};

export default TreatmentTracker;
